// /moj-translator/documents — Wave 67: TH hub of court/MoJ translation document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/moj-translator/documents";
const URL = `${SITE}${PATH}`;
const TITLE = "เอกสารคดีที่ต้องแปลและรับรองคำแปล — 6 กลุ่ม | NYC";
const DESC =
  "รวม 6 กลุ่มเอกสารคดีที่ต้องมีคำแปลรับรองสำหรับศาลไทยและงานบังคับคดี พร้อมลำดับขั้นตอน เช็กลิสต์ และคำถามที่พบบ่อย อ้างอิง ป.วิ.แพ่ง มาตรา 46 และกรมการกงสุล";

export const Route = createFileRoute("/moj-translator/documents/")({
  loader: async () => {
    const { MOJ_DOC_GROUPS } = await import("@/data/moj-doc-groups");
    return {
      __d: { MOJ_DOC_GROUPS },
      groups: MOJ_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleTh,
        faqs: g.faqsTh.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: URL },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/moj-translator/documents` },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "th",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "กลุ่มเอกสารคดีที่ต้องแปลและรับรองคำแปล",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: MojDocumentsPage,
});

function MojDocumentsPage() {
  const { __d: { MOJ_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/moj-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/moj-translator" className="hover:underline">
          นักแปลขึ้นทะเบียนกระทรวงยุติธรรม
        </Link>{" "}
        / เอกสารคดีที่รับแปลและรับรอง
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        เอกสารคดีที่ต้องแปลและรับรองคำแปล — 6 กลุ่ม
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 กำหนดให้เอกสารภาษาต่างประเทศที่ใช้ในกระบวนพิจารณา
        ต้องมีคำแปลภาษาไทยพร้อมคำรับรองความถูกต้องของผู้แปล หน้านี้จัดกลุ่มเอกสารตามลักษณะการใช้งานในคดี
        พร้อมลำดับขั้นตอนและเช็กลิสต์ของแต่ละกลุ่ม โดยรายละเอียดปลีกย่อยอาจต่างกันตามศาลหรือหน่วยงานที่รับเรื่อง
        จึงควรตรวจสอบกับหน่วยงานนั้นก่อนยื่นทุกครั้ง
      </p>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {MOJ_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/moj-translator/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleTh}
            </Link>
          </li>
        ))}
      </ul>

      {MOJ_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleTh}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryTh}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyTh.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/moj-translator/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            อ่านคู่มือ {g.titleTh} ฉบับเต็ม →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        ต้องการให้เจ้าหน้าที่ประเมินชุดเอกสารของคุณ ส่งภาพสแกนมาก่อนได้ทางโทรศัพท์ LINE หรืออีเมล
        แล้วทีมงานจะแจ้งขั้นตอนและค่าบริการที่ตรงกับกรณีของคุณกลับไป
      </p>
          <ServiceAuthorityBlock topic="moj" locale="th" />
    </main>
  );
}
